"use client";

import React from "react";
import Image, { type StaticImageData } from "next/image";
import { Images } from "@/app/assets/data/images";

interface IdeaCtaProps {
  bg?: string;
  bgImage?: string | StaticImageData;
  title1?: string;
  title2?: React.ReactNode;
  description?: React.ReactNode;
  button?: React.ReactNode;
  variant?: "black" | "white";
}

export default function IdeaCta({
  title1,
  title2,
  description,
  button,
  variant = "black",
}: IdeaCtaProps) {
  const isWhite = variant === "white";

  return (
    <section
      className={`idea-cta relative z-2 flex items-center justify-center overflow-hidden py-[50px] lg:py-[90px] ${isWhite ? "bg-[#f2f4fa]" : "bg-black"}`}
    >
      {!isWhite && (
        <Image
          src={Images.ideaCtaBg}
          alt="CTA Background"
          fill
          className="object-cover object-center"
          quality={90}
        />
      )}
      {!isWhite && <div className="absolute inset-0 z-[1] bg-black/30" />}

      <div
        className={`relative z-10 mx-auto w-[calc(100%_-_10%)] max-w-[2500px] ${isWhite ? "lg:px-[14px]" : ""}`}
      >
        {isWhite ? (
          <div className="group relative">
            {/* Ambient hover glow behind card */}
            <div className="pointer-events-none absolute -inset-[1px] rounded-[26px] bg-[#225bd4]/0 opacity-0 blur-[24px] transition-all duration-700 ease-out group-hover:bg-[#225bd4]/[0.12] group-hover:opacity-100 lg:rounded-[34px]" />

            <div className="absolute inset-0 translate-x-[10px] translate-y-[10px] rounded-[24px] bg-[#c7d2e0] transition-all duration-700 ease-out group-hover:translate-x-[14px] group-hover:translate-y-[14px] group-hover:bg-[#b9c8dc] lg:translate-x-[16px] lg:translate-y-[16px] lg:rounded-[32px]" />

            <div className="relative overflow-hidden rounded-[24px] border-[1.5px] border-[#d9e2ed] bg-white px-6 py-[46px] transition-all duration-700 ease-out group-hover:-translate-x-[3px] group-hover:-translate-y-[4px] group-hover:border-[#225bd4]/30 group-hover:shadow-[0_24px_70px_rgba(34,91,212,0.10)] lg:rounded-[32px] lg:px-[64px] lg:py-[64px]">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_80%_at_50%_110%,rgba(34,91,212,0.12)_0%,rgba(34,91,212,0.035)_45%,transparent_75%)] opacity-60 transition-all duration-1000 ease-out group-hover:scale-110 group-hover:opacity-100" />

              <div className="pointer-events-none absolute left-1/2 top-[50%] h-[180px] w-[180px] -translate-x-1/2 -translate-y-1/2 scale-75 rounded-full border border-[#225bd4]/0 opacity-0 transition-all duration-[1200ms] ease-out group-hover:scale-100 group-hover:border-[#225bd4]/[0.09] group-hover:opacity-100" />
              <div className="pointer-events-none absolute left-[18%] right-[18%] top-0 h-px bg-gradient-to-r from-transparent via-[#225bd4]/0 to-transparent opacity-0 transition-all duration-700 ease-out group-hover:left-[10%] group-hover:right-[10%] group-hover:bg-gradient-to-r group-hover:from-transparent group-hover:via-[#225bd4]/40 group-hover:to-transparent group-hover:opacity-100" />

              {/* Content */}
              <div className="relative z-10 mx-auto flex max-w-[1200px]  flex-col items-center gap-[22px] text-center transition-transform duration-700 ease-out group-hover:-translate-y-[2px]">
                <h2 className="leading-[102%] text-[length:var(--section-title)]">
                  <span
                    className="clash-font font-medium capitalize leading-[92%] text-[#00040B] transition-colors duration-500 group-hover:text-[#071b4d]"
                    dangerouslySetInnerHTML={{ __html: title1 ?? "" }}
                  />
                  {title2 && (
                    <span className="ml-2 clash-font font-medium capitalize leading-[92%] text-[#00040B] transition-colors duration-500 group-hover:text-[#071b4d]">
                      {title2}
                    </span>
                  )}
                </h2>

                <p className="text-center text-[length:var(--hero-para)] font-normal leading-[150%] text-[#00040B]/55 transition-colors duration-500 group-hover:text-[#00040B]/65">
                  {description}
                </p>

                <div className="mt-4 flex justify-center transition-transform duration-500 ease-out group-hover:-translate-y-1">
                  {button}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="group relative overflow-hidden rounded-[20px] border border-white/[0.15] px-6 py-[50px] transition-all duration-700 ease-out hover:-translate-y-1 hover:border-[#225bd4]/40 hover:shadow-[0_20px_80px_rgba(34,91,212,0.12)] lg:rounded-[32px] lg:px-[60px] lg:py-[70px]"
            style={{
              background:
                "radial-gradient(ellipse 60% 90% at 50% 110%, rgba(34,91,212,0.45) 0%, rgba(34,91,212,0.1) 50%, transparent 70%), rgba(4,8,20,0.6)",
            }}
          >
            <div
              className={`pointer-events-none absolute left-1/2 top-full h-[350px] w-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#225bd4]/20 opacity-40 blur-[100px] transition-all duration-1000 ease-out group-hover:w-[75%] group-hover:bg-[#225bd4]/30 group-hover:opacity-80`}
            />
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[180px] w-[180px] -translate-x-1/2 -translate-y-1/2 scale-90 rounded-full border border-[#225bd4]/10 opacity-0 transition-all duration-1000 ease-out group-hover:scale-[1.5] group-hover:opacity-100" />
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 scale-75 rounded-full border border-white/[0.03] opacity-0 transition-all duration-[1200ms] ease-out group-hover:scale-100 group-hover:opacity-100" />
            <div className="pointer-events-none absolute left-[12%] right-[12%] top-0 h-px bg-gradient-to-r from-transparent via-[#225bd4]/40 to-transparent opacity-70 blur-[0.5px] transition-all duration-700 group-hover:left-[8%] group-hover:right-[8%] group-hover:opacity-100" />

            <div className="relative z-10 mx-auto flex max-w-[1000px] flex-col items-center gap-[20px] text-center">
              <h2 className="leading-[100%] text-[length:var(--section-title)]">
                <span className="clash-font font-medium capitalize leading-[90%] text-white transition-all duration-500 group-hover:tracking-[-0.01em]">
                  {title1}
                </span>
                {title2 && (
                  <span className="ml-2 clash-font font-medium capitalize leading-[90%] text-white">
                    {title2}
                  </span>
                )}
              </h2>

              <p className="w-full max-w-[70ch] text-center text-[length:var(--hero-para)] font-normal leading-[150%] text-white/70 transition-all duration-700 group-hover:text-white/85">
                {description}
              </p>

              <div className="mt-4 transition-all duration-500 group-hover:-translate-y-1 lg:mt-6">
                {button}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
